import React from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Icon,
  Link,
  SimpleGrid,
  Stack,
  Tag,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  MdSchedule,
  MdPeopleAlt,
  MdAutoAwesome,
  MdShield,
  MdRocketLaunch,
  MdInsights,
  MdArrowForward,
  MdEmail,
  MdLocationOn,
  MdSupport,
} from 'react-icons/md';
import { useUserState } from 'context/UserContext';
import { useTranslation } from 'i18n';
import { PLATFORMS } from './integrations/data';

const STEPS = [
  { step: '01', titleKey: 'how.step1.title', descKey: 'how.step1.desc' },
  { step: '02', titleKey: 'how.step2.title', descKey: 'how.step2.desc' },
  { step: '03', titleKey: 'how.step3.title', descKey: 'how.step3.desc' },
];

const FEATURES = [
  {
    icon: MdPeopleAlt,
    titleKey: 'features.multiAccount.title',
    descKey: 'features.multiAccount.desc',
    color: 'blue',
  },
  {
    icon: MdSchedule,
    titleKey: 'features.scheduling.title',
    descKey: 'features.scheduling.desc',
    color: 'purple',
  },
  {
    icon: MdAutoAwesome,
    titleKey: 'features.ai.title',
    descKey: 'features.ai.desc',
    color: 'pink',
  },
  {
    icon: MdShield,
    titleKey: 'features.security.title',
    descKey: 'features.security.desc',
    color: 'green',
  },
  {
    icon: MdInsights,
    titleKey: 'features.analytics.title',
    descKey: 'features.analytics.desc',
    color: 'orange',
  },
  {
    icon: MdRocketLaunch,
    titleKey: 'features.crossPost.title',
    descKey: 'features.crossPost.desc',
    color: 'red',
  },
];

export default function Site() {
  const { isAuthenticated } = useUserState();
  const { t } = useTranslation();

  return (
    <Box>
      {/* ===== Hero ===== */}
      <Box
        position="relative"
        overflow="hidden"
        bgGradient="linear(to-br, blue.50, purple.50)"
        py={{ base: 12, sm: 16, md: 24 }}
      >
        <Box
          position="absolute"
          top="-100px"
          right="-100px"
          w="400px"
          h="400px"
          borderRadius="full"
          bg="purple.100"
          opacity={0.4}
          filter="blur(60px)"
          pointerEvents="none"
        />
        <Box
          position="absolute"
          bottom="-150px"
          left="-100px"
          w="500px"
          h="500px"
          borderRadius="full"
          bg="blue.100"
          opacity={0.5}
          filter="blur(80px)"
          pointerEvents="none"
        />

        <Container maxW="container.xl" position="relative" px={{ base: 4, md: 6 }}>
          <Stack
            spacing={{ base: 5, md: 6 }}
            maxW="3xl"
            mx="auto"
            align="center"
            textAlign="center"
          >
            <Tag size="md" colorScheme="blue" borderRadius="full" px={4} py={1}>
              <Text fontSize="xs" fontWeight="600">
                {t('hero.badge')}
              </Text>
            </Tag>
            <Heading
              fontSize={{ base: '2xl', sm: '3xl', md: '5xl', lg: '6xl' }}
              fontWeight={800}
              lineHeight="1.1"
              color="gray.900"
            >
              {t('hero.title.line1')}
              <Box
                as="span"
                bgGradient="linear(to-r, blue.500, purple.500)"
                bgClip="text"
                display="block"
              >
                {t('hero.title.line2')}
              </Box>
            </Heading>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="2xl">
              {t('hero.subtitle')}
            </Text>
            <HStack spacing={3} pt={2} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to={isAuthenticated ? '/admin/dashboard' : '/auth/sign-in'}
                size="lg"
                colorScheme="blue"
                rightIcon={<MdArrowForward />}
                borderRadius="full"
                px={8}
                boxShadow="lg"
              >
                {isAuthenticated ? t('hero.cta.dashboard') : t('hero.cta.signIn')}
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500" pt={2}>
              {t('hero.trust')}
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* ===== Platforms ===== */}
      <Container
        maxW="container.xl"
        id="platforms"
        px={{ base: 4, md: 6 }}
        py={{ base: 12, md: 20 }}
      >
        <Stack spacing={3} mb={{ base: 8, md: 10 }} align="center" textAlign="center">
          <Tag size="sm" colorScheme="purple" borderRadius="full" px={3}>
            <Text fontSize="xs" fontWeight="600">
              {t('platforms.badge')}
            </Text>
          </Tag>
          <Heading
            fontSize={{ base: 'xl', sm: '2xl', md: '3xl' }}
            fontWeight={800}
            color="gray.900"
          >
            {t('platforms.title')}
          </Heading>
          <Text color="gray.600" maxW="xl">
            {t('platforms.subtitle')}
          </Text>
        </Stack>

        <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={{ base: 3, md: 4 }}>
          {PLATFORMS.map(p => {
            const IconComp = p.icon;
            const hasShowcase = (p.screenshots?.length || 0) > 0;
            const taglineKey = `platform.${p.slug}.tagline`;
            const tagline = t(taglineKey);
            return (
              <Box
                key={p.slug}
                as={RouterLink}
                to={`/integrations/${p.slug}`}
                bg="white"
                border="1px solid"
                borderColor="gray.100"
                borderRadius="14px"
                p={{ base: 4, md: 6 }}
                textAlign="center"
                transition="all 0.2s"
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: 'xl',
                  borderColor: `${p.color}30`,
                  textDecoration: 'none',
                }}
                position="relative"
              >
                {hasShowcase && (
                  <Tag
                    size="sm"
                    colorScheme="green"
                    position="absolute"
                    top={2}
                    right={2}
                    borderRadius="full"
                    fontSize="9px"
                    fontWeight={700}
                  >
                    {t('platforms.live')}
                  </Tag>
                )}
                <Flex
                  w={{ base: '48px', md: '56px' }}
                  h={{ base: '48px', md: '56px' }}
                  mx="auto"
                  borderRadius="14px"
                  bg={`${p.color}15`}
                  align="center"
                  justify="center"
                  color={p.color}
                  mb={3}
                >
                  <IconComp size={24} />
                </Flex>
                <Text fontWeight={700} fontSize={{ base: 'sm', md: 'md' }} color="gray.800">
                  {p.name}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1} noOfLines={1}>
                  {tagline === taglineKey ? p.tagline : tagline}
                </Text>
              </Box>
            );
          })}
        </SimpleGrid>

        <Flex justify="center" pt={{ base: 6, md: 8 }}>
          <Button
            as={RouterLink}
            to="/integrations"
            variant="ghost"
            colorScheme="blue"
            size="sm"
            rightIcon={<MdArrowForward />}
          >
            {t('platforms.viewAll')}
          </Button>
        </Flex>
      </Container>

      {/* ===== Features ===== */}
      <Box bg="gray.50" py={{ base: 12, md: 20 }} id="features">
        <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
          <Stack spacing={3} mb={{ base: 8, md: 12 }} align="center" textAlign="center">
            <Tag size="sm" colorScheme="blue" borderRadius="full" px={3}>
              <Text fontSize="xs" fontWeight="600">
                {t('features.badge')}
              </Text>
            </Tag>
            <Heading
              fontSize={{ base: 'xl', sm: '2xl', md: '3xl' }}
              fontWeight={800}
              color="gray.900"
            >
              {t('features.title')}
            </Heading>
            <Text color="gray.600" maxW="xl">
              {t('features.subtitle')}
            </Text>
          </Stack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 4, md: 5 }}>
            {FEATURES.map(f => (
              <Box
                key={f.titleKey}
                bg="white"
                borderRadius="14px"
                p={{ base: 5, md: 6 }}
                transition="all 0.2s"
                _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }}
              >
                <Flex
                  w="44px"
                  h="44px"
                  borderRadius="10px"
                  bg={`${f.color}.50`}
                  color={`${f.color}.500`}
                  align="center"
                  justify="center"
                  mb={4}
                >
                  <Icon as={f.icon} boxSize={6} />
                </Flex>
                <Heading fontSize="lg" fontWeight={700} color="gray.900" mb={2}>
                  {t(f.titleKey)}
                </Heading>
                <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                  {t(f.descKey)}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ===== How it works ===== */}
      <Container
        maxW="container.xl"
        id="how-it-works"
        px={{ base: 4, md: 6 }}
        py={{ base: 12, md: 20 }}
      >
        <Stack spacing={3} mb={{ base: 8, md: 12 }} align="center" textAlign="center">
          <Tag size="sm" colorScheme="green" borderRadius="full" px={3}>
            <Text fontSize="xs" fontWeight="600">
              {t('how.badge')}
            </Text>
          </Tag>
          <Heading
            fontSize={{ base: 'xl', sm: '2xl', md: '3xl' }}
            fontWeight={800}
            color="gray.900"
          >
            {t('how.title')}
          </Heading>
          <Text color="gray.600" maxW="xl">
            {t('how.subtitle')}
          </Text>
        </Stack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 5, md: 6 }}>
          {STEPS.map((s, idx) => (
            <Box
              key={s.step}
              bg="white"
              borderRadius="14px"
              p={{ base: 5, md: 6 }}
              border="1px solid"
              borderColor="gray.100"
              position="relative"
              transition="all 0.2s"
              _hover={{ borderColor: 'blue.200', boxShadow: 'md' }}
            >
              <Text
                fontSize={{ base: '4xl', md: '5xl' }}
                fontWeight={900}
                bgGradient="linear(to-br, blue.400, purple.500)"
                bgClip="text"
                lineHeight="1"
                mb={3}
              >
                {s.step}
              </Text>
              <Heading fontSize="lg" fontWeight={700} color="gray.900" mb={2}>
                {t(s.titleKey)}
              </Heading>
              <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                {t(s.descKey)}
              </Text>
              {idx < STEPS.length - 1 && (
                <Icon
                  as={MdArrowForward}
                  color="gray.300"
                  boxSize={6}
                  position="absolute"
                  top="50%"
                  right="-14px"
                  transform="translateY(-50%)"
                  display={{ base: 'none', md: 'block' }}
                />
              )}
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* ===== Contact ===== */}
      <Box bg="gray.50" py={{ base: 12, md: 20 }} id="contact">
        <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
          <Stack spacing={3} mb={{ base: 8, md: 10 }} align="center" textAlign="center">
            <Tag size="sm" colorScheme="orange" borderRadius="full" px={3}>
              <Text fontSize="xs" fontWeight="600">
                {t('contact.badge')}
              </Text>
            </Tag>
            <Heading
              fontSize={{ base: 'xl', sm: '2xl', md: '3xl' }}
              fontWeight={800}
              color="gray.900"
            >
              {t('contact.title')}
            </Heading>
            <Text color="gray.600" maxW="xl">
              {t('contact.subtitle')}
            </Text>
          </Stack>

          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing={{ base: 4, md: 5 }}
            maxW="4xl"
            mx="auto"
          >
            <Box
              bg="white"
              borderRadius="14px"
              p={{ base: 5, md: 6 }}
              textAlign="center"
              border="1px solid"
              borderColor="gray.100"
            >
              <Flex
                w="48px"
                h="48px"
                mx="auto"
                borderRadius="12px"
                bg="blue.50"
                color="blue.500"
                align="center"
                justify="center"
                mb={3}
              >
                <Icon as={MdEmail} boxSize={6} />
              </Flex>
              <Text fontWeight={700} color="gray.800" mb={1}>
                {t('contact.email.label')}
              </Text>
              <Link
                href="mailto:admin@waebox.com"
                fontSize="sm"
                color="blue.500"
                _hover={{ textDecoration: 'underline' }}
              >
                admin@waebox.com
              </Link>
            </Box>

            <Box
              bg="white"
              borderRadius="14px"
              p={{ base: 5, md: 6 }}
              textAlign="center"
              border="1px solid"
              borderColor="gray.100"
            >
              <Flex
                w="48px"
                h="48px"
                mx="auto"
                borderRadius="12px"
                bg="purple.50"
                color="purple.500"
                align="center"
                justify="center"
                mb={3}
              >
                <Icon as={MdSupport} boxSize={6} />
              </Flex>
              <Text fontWeight={700} color="gray.800" mb={1}>
                {t('contact.support.label')}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {t('contact.support.desc')}
              </Text>
              <Link
                as={RouterLink}
                to="/legal"
                fontSize="sm"
                color="purple.500"
                _hover={{ textDecoration: 'underline' }}
                display="inline-block"
                mt={1}
              >
                {t('contact.support.cta')}
              </Link>
            </Box>

            <Box
              bg="white"
              borderRadius="14px"
              p={{ base: 5, md: 6 }}
              textAlign="center"
              border="1px solid"
              borderColor="gray.100"
            >
              <Flex
                w="48px"
                h="48px"
                mx="auto"
                borderRadius="12px"
                bg="green.50"
                color="green.500"
                align="center"
                justify="center"
                mb={3}
              >
                <Icon as={MdLocationOn} boxSize={6} />
              </Flex>
              <Text fontWeight={700} color="gray.800" mb={1}>
                {t('contact.location.label')}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {t('contact.location.company')}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {t('contact.location.country')}
              </Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ===== CTA banner ===== */}
      <Container maxW="container.xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 20 }}>
        <Box
          bgGradient="linear(to-r, blue.500, purple.500)"
          borderRadius={{ base: '16px', md: '20px' }}
          p={{ base: 6, sm: 8, md: 12 }}
          textAlign="center"
          color="white"
          boxShadow="2xl"
        >
          <Heading fontSize={{ base: 'xl', sm: '2xl', md: '3xl' }} fontWeight={800} mb={3}>
            {t('cta.title')}
          </Heading>
          <Text fontSize={{ base: 'sm', md: 'md' }} opacity={0.9} maxW="xl" mx="auto" mb={6}>
            {t('cta.subtitle')}
          </Text>
          <Button
            as={RouterLink}
            to={isAuthenticated ? '/admin/dashboard' : '/auth/sign-in'}
            size="lg"
            bg="white"
            color="blue.600"
            _hover={{ bg: 'gray.100' }}
            rightIcon={<MdArrowForward />}
            borderRadius="full"
            px={8}
          >
            {isAuthenticated ? t('cta.dashboard') : t('cta.signIn')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
